import React, { useEffect, useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { AttendanceSidepanelCompensationBlockFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'


import AllocationInputs from './AllocationInputs'
import AllocationSummary from './AllocationSummary'

type Props = {
  compensationBlocks?: AttendanceSidepanelCompensationBlockFragment[]
  bohBlock: AttendanceSidepanelCompensationBlockFragment
  onHasErrorsChange?: (hasErrors: boolean) => void
  payrollCompensationEnabled: boolean
  timeoffCompensationEnabled: boolean
  /** When provided, component is controlled (allocation state lifted to parent for mutation payload) */
  timeOffMinutes?: number
  payrollMinutes?: number
  onTimeOffChange?: (minutes: number) => void
  onPayrollChange?: (minutes: number) => void
}

const FlexibleCompensationAllocation = ({
  bohBlock,
  onHasErrorsChange,
  payrollCompensationEnabled,
  timeoffCompensationEnabled,
  timeOffMinutes: controlledTimeOffMinutes,
  payrollMinutes: controlledPayrollMinutes,
  onTimeOffChange,
  onPayrollChange,
}: Props) => {


  const [internalTimeOff, setInternalTimeOff] = useState(0)
  const [internalPayroll, setInternalPayroll] = useState(0)

  const isControlled = onTimeOffChange !== undefined && onPayrollChange !== undefined
  const timeOffMinutes = isControlled ? controlledTimeOffMinutes ?? 0 : internalTimeOff
  const setTimeOffMinutes = isControlled ? onTimeOffChange ?? (() => {}) : setInternalTimeOff
  const payrollMinutes = isControlled ? controlledPayrollMinutes ?? 0 : internalPayroll
  const setPayrollMinutes = isControlled ? onPayrollChange ?? (() => {}) : setInternalPayroll

  // Get available BOH minutes from the selected block
  const availableBOHMinutes = Number(bohBlock.minutes) || 0

  const timeOffMaxMinutes = undefined

  // Validation errors - calculate in real-time
  const totalAllocated = timeOffMinutes + payrollMinutes
  const exceedsBalance = totalAllocated > availableBOHMinutes

  const errors = useMemo(() => {
    return {
      exceedsBalance: exceedsBalance
        ? i18n.t(
            'clock_in.sidepanel.compensation_overview.flexible_compensation.error.exceeds_balance'
          )
        : undefined,
    }
  }, [exceedsBalance])

  useEffect(() => {
    onHasErrorsChange?.(exceedsBalance)

    return () => {
      onHasErrorsChange?.(false)
    }
  }, [exceedsBalance, onHasErrorsChange])

  if (availableBOHMinutes <= 0) {
    return null
  }

  const allocatedMinutes = timeOffMinutes + payrollMinutes

  return (
    <Box padding="s12" flexDirection="column" gap="s16">
      <Text.Normal size="300">
        {i18n.t(
          'clock_in.sidepanel.compensation_overview.flexible_compensation.allocate_instruction'
        )}
      </Text.Normal>

      <AllocationInputs
        timeOffMinutes={timeOffMinutes}
        payrollMinutes={payrollMinutes}
        availableMinutes={availableBOHMinutes}
        timeOffMaxMinutes={timeOffMaxMinutes}
        payrollEnabled={payrollCompensationEnabled}
        timeOffEnabled={timeoffCompensationEnabled}
        onTimeOffChange={setTimeOffMinutes}
        onPayrollChange={setPayrollMinutes}
        errors={errors}
        exceedsBalance={exceedsBalance}
      />

      <AllocationSummary
        allocatedMinutes={allocatedMinutes}
        totalAvailableMinutes={availableBOHMinutes}
        hasError={exceedsBalance}
      />
    </Box>
  )
}

export default FlexibleCompensationAllocation
